/* eslint-disable no-console */
import {useEffect, useRef, useState} from 'react';
import type {FaceMesh, Results, NormalizedLandmark} from '@mediapipe/face_mesh';
import {useBiometricWizard} from '../../../hooks/useBiometricWizard';
import {useFacePhotoStore} from '../../../store';
import {extractBase64} from '../../../utils';
import {translations} from 'presentation/translations-new';
import {STEP_CONFIG} from '../../../constants/photo-step-config';

const {errorMessage, holdPosition} =
	translations['pt-br'].others.facialBiometric.facePhoto.faceCamera;

export function useFaceDetection() {
	const [modelsLoaded, setModelsLoaded] = useState(false);
	const faceMeshRef = useRef<FaceMesh | null>(null);
	const lastResultRef = useRef<Results | null>(null);
	const isProcessingRef = useRef(false);
	const videoRef = useRef<HTMLVideoElement>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const stableStartRef = useRef<number | null>(null);
	const capturedRef = useRef(false);
	const [message, setMessage] = useState(STEP_CONFIG.front.title);
	const [faceDetected, setFaceDetected] = useState(false);
	const [photo, setPhoto] = useState<string | null>(null);
	const [error, setError] = useState<string>('');
	const [step, setStep] = useState<'front' | 'right' | 'left'>('front');

	const {setFrontFace, setRightFace, setLeftFace} = useFacePhotoStore(state => state);

	const {next} = useBiometricWizard();

	useEffect(() => {
		loadModel();

		return () => {
			faceMeshRef.current?.close();
			faceMeshRef.current = null;
		};
	}, []);

	const loadModel = async () => {
		try {
			const {FaceMesh} = await import('@mediapipe/face_mesh');

			const faceMesh = new FaceMesh({
				locateFile: file => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
			});

			faceMesh.setOptions({
				maxNumFaces: 1,
				refineLandmarks: true,
				minDetectionConfidence: 0.6,
				minTrackingConfidence: 0.6,
			});

			faceMesh.onResults(results => {
				lastResultRef.current = results;
			});

			faceMeshRef.current = faceMesh;

			setModelsLoaded(true);
		} catch (error) {
			console.error('Error loading FaceMesh:', error);
		}
	};

	function isFaceAtGoodDistance(landmarks: NormalizedLandmark[]) {
		const leftCheek = landmarks[234];
		const rightCheek = landmarks[454];

		if (!leftCheek || !rightCheek) return false;

		const faceWidth = Math.abs(rightCheek.x - leftCheek.x);
		return faceWidth >= 0.3;
	}

	const isFaceCentered = (landmarks: NormalizedLandmark[], w: number, h: number) => {
		const top = landmarks[10];
		const chin = landmarks[152];
		const left = landmarks[234];
		const right = landmarks[454];

		if (!top || !chin || !left || !right) return false;

		const centerX = ((left.x + right.x) / 2) * w;

		const centerY = ((top.y + chin.y) / 2) * h;

		const oval = {
			cx: w / 2,
			cy: h / 2,
			rx: w * 0.35,
			ry: h * 0.35,
		};

		const centerZone = {
			rx: oval.rx * 0.2,
			ry: oval.ry * 0.2,
		};

		const dx = (centerX - oval.cx) ** 2 / centerZone.rx ** 2;
		const dy = (centerY - oval.cy) ** 2 / centerZone.ry ** 2;

		return dx + dy <= 1;
	};

	function getFaceDirection(landmarks: NormalizedLandmark[]) {
		const leftEye = landmarks[33];
		const rightEye = landmarks[263];
		const nose = landmarks[1];

		if (!leftEye || !rightEye || !nose) return 'unknown';

		const eyeCenterX = (leftEye.x + rightEye.x) / 2;
		const tolerance = Math.abs(leftEye.x - rightEye.x) * 0.2;

		if (nose.x < eyeCenterX - tolerance) return 'right';
		if (nose.x > eyeCenterX + tolerance) return 'left';
		return 'front';
	}

	const detectFace = async (video: HTMLVideoElement) => {
		if (!modelsLoaded || !faceMeshRef.current) return null;
		if (!video.videoWidth || !video.videoHeight) return null;
		if (isProcessingRef.current) return null;

		isProcessingRef.current = true;

		try {
			await faceMeshRef.current.send({image: video});

			const results = lastResultRef.current;

			if (results?.multiFaceLandmarks?.length) {
				const landmarks = results.multiFaceLandmarks[0];
				const frameWidth = video.videoWidth;
				const frameHeight = video.videoHeight;

				const distance = isFaceAtGoodDistance(landmarks);
				const centered = isFaceCentered(landmarks, frameWidth, frameHeight);
				const direction = getFaceDirection(landmarks);

				return centered && distance && direction === step;
			}

			return false;
		} catch (err) {
			return null;
		} finally {
			isProcessingRef.current = false;
		}
	};

	const startCamera = async () => {
		try {
			stopCamera();

			capturedRef.current = false;
			stableStartRef.current = null;

			const stream = await navigator.mediaDevices.getUserMedia({
				video: {width: 640, height: 480, facingMode: 'user'},
			});

			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				streamRef.current = stream;

				await videoRef.current.play();

				// eslint-disable-next-line consistent-return
				await new Promise<void>(resolve => {
					if (videoRef.current!.videoWidth > 0) return resolve();
					videoRef.current!.onloadedmetadata = () => resolve();
				});
			}
		} catch (err) {
			console.error('Erro ao iniciar câmera:', err);
		}
	};

	const stopCamera = () => {
		streamRef.current?.getTracks().forEach(t => t.stop());
		streamRef.current = null;
	};

	const capturePhoto = async () => {
		if (!videoRef.current) return;

		const canvas = document.createElement('canvas');
		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		canvas.width = videoRef.current.videoWidth;
		canvas.height = videoRef.current.videoHeight;

		ctx.drawImage(videoRef.current, 0, 0);

		const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

		setPhoto(dataUrl);
		stopCamera();
	};

	useEffect(() => {
		if (!modelsLoaded) return;

		let rafId: number;

		const detectLoop = async () => {
			const video = videoRef.current;

			if (!video || !video.videoWidth || photo) {
				rafId = requestAnimationFrame(detectLoop);
				return;
			}

			const detection = await detectFace(video);
			const now = Date.now();

			if (detection) {
				setFaceDetected(true);

				if (!stableStartRef.current) {
					stableStartRef.current = now;
					setMessage(holdPosition);
				}

				const elapsed = now - stableStartRef.current;

				if (elapsed >= 3000 && !capturedRef.current) {
					capturedRef.current = true;

					await capturePhoto();
				}
			} else {
				setFaceDetected(false);
				stableStartRef.current = null;
				setMessage(STEP_CONFIG[step].title);
			}

			rafId = requestAnimationFrame(detectLoop);
		};

		detectLoop();
		// eslint-disable-next-line consistent-return
		return () => cancelAnimationFrame(rafId);
	}, [modelsLoaded, photo]);

	useEffect(() => {
		startCamera();
		return stopCamera;
	}, []);

	const nextStep = async () => {
		const photoBase64 = extractBase64(photo ?? undefined);

		if (!photoBase64) {
			setError(errorMessage);
			return;
		}

		if (step === 'front') {
			setStep('right');
			setMessage(STEP_CONFIG.right.title);
			setFrontFace(photoBase64);
		}

		if (step === 'right') {
			setStep('left');
			setMessage(STEP_CONFIG.left.title);
			setRightFace(photoBase64);
		}

		if (step === 'left') {
			setLeftFace(photoBase64);
			setStep('front');
			setPhoto(null);
			startCamera();
			next();
			return;
		}

		setPhoto(null);

		startCamera();
	};

	const retakePhoto = () => {
		if (photo) URL.revokeObjectURL(photo);

		setPhoto(null);
		setFaceDetected(false);
		setMessage(STEP_CONFIG[step].title);

		capturedRef.current = false;
		stableStartRef.current = null;

		startCamera();
	};

	return {
		videoRef,
		faceDetected,
		message,
		retakePhoto,
		nextStep,
		photo,
		error,
		step,
	};
}
